import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx/xlsx.mjs';
import { SchemaFieldMeta } from '../content-type/schema-meta.model';

export interface SchemaNode {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object';
  mandatory?: boolean;
  description?: string;
  meta?: SchemaFieldMeta;
  access?: Record<string, string[]>;
  dateFormat?: string;
  items?: SchemaNode;
  properties?: SchemaNode[];
  validators?: Array<Record<string, unknown>>;
}

export interface SpreadsheetRows {
  fileName: string;
  headers: string[];
  rows: Array<{ rowNumber: number; values: Record<string, unknown> }>;
}

/** A schema-import error that the UI can translate without parsing display text. */
export class SchemaImportError extends Error {
  constructor(
    readonly translationKey: string,
    readonly translationParams: Record<string, string> = {}
  ) {
    super(translationKey);
  }
}

/** Workbook parsing shared by the schema editor and the Tridy importer. */
@Injectable({ providedIn: 'root' })
export class ExcelImportService {
  private readonly schemaHeaderNames = new Set([
    'key', 'field', 'fieldname', 'name', 'property', 'type', 'datatype',
    'description', 'mandatory', 'required', 'listitemstype', 'itemtype',
    'items', 'arraytype', 'dateformat', 'format', 'validator', 'validators', 'meta', 'access'
  ]);

  async readSchema(file: File): Promise<SchemaNode[]> {
    const rows = await this.readRows(file);
    const headerIndex = this.findSchemaHeader(rows);
    if (headerIndex < 0) {
      throw new SchemaImportError('contentTypes.schemaImportNoHeader');
    }

    const headers = this.normaliseHeaders(rows[headerIndex]);
    const keyIndex = this.findHeader(headers, ['key', 'field', 'fieldname', 'name', 'property']);
    const typeIndex = this.findHeader(headers, ['type', 'datatype']);
    if (keyIndex < 0 || typeIndex < 0) {
      throw new SchemaImportError('contentTypes.schemaImportMissingColumns');
    }

    const descriptionIndex = this.findHeader(headers, ['description']);
    const mandatoryIndex = this.findHeader(headers, ['mandatory', 'required']);
    const itemsIndex = this.findHeader(headers, ['listitemstype', 'itemtype', 'items', 'arraytype']);
    const formatIndex = this.findHeader(headers, ['dateformat', 'format']);
    const validatorsIndex = this.findHeader(headers, ['validator', 'validators']);
    const metaIndex = this.findHeader(headers, ['meta']);
    const accessIndex = this.findHeader(headers, ['access']);
    const properties: SchemaNode[] = [];

    rows.slice(headerIndex + 1).forEach(row => {
      const key = this.asText(row[keyIndex]);
      if (!key) return;
      // Notes and instructions are commonly placed below a schema table in a
      // single merged cell. They are not schema fields.
      if (!this.asText(row[typeIndex]) && row.filter(value => this.asText(value)).length <= 1) return;
      const type = this.normaliseType(row[typeIndex]);
      if (!type) throw new SchemaImportError('contentTypes.schemaImportUnsupportedType', { key });

      const node: SchemaNode = { key: key.split('.').pop()!, type };
      const description = descriptionIndex >= 0 ? this.asText(row[descriptionIndex]) : '';
      const dateFormat = formatIndex >= 0 ? this.asText(row[formatIndex]) : '';
      if (description) node.description = description;
      if (dateFormat) node.dateFormat = dateFormat;
      if (mandatoryIndex >= 0) node.mandatory = this.asBoolean(row[mandatoryIndex]);
      if (validatorsIndex >= 0 && this.asText(row[validatorsIndex])) {
        try {
          const validators = JSON.parse(this.asText(row[validatorsIndex]));
          if (!Array.isArray(validators)) throw new Error();
          node.validators = validators;
        } catch {
          throw new SchemaImportError('contentTypes.schemaImportInvalidValidators', { key });
        }
      }
      if (metaIndex >= 0 && this.asText(row[metaIndex])) {
        try {
          const meta = JSON.parse(this.asText(row[metaIndex]));
          if (!meta || typeof meta !== 'object' || Array.isArray(meta)) throw new Error();
          node.meta = meta as SchemaFieldMeta;
        } catch {
          throw new SchemaImportError('contentTypes.schemaImportInvalidMeta', { key });
        }
      }
      if (accessIndex >= 0 && this.asText(row[accessIndex])) {
        try {
          const access = JSON.parse(this.asText(row[accessIndex]));
          if (!access || typeof access !== 'object' || Array.isArray(access) ||
            !Object.entries(access).every(([role, permissions]) =>
              role !== '__proto__' && role !== 'constructor' && role !== 'prototype' &&
              Array.isArray(permissions) && permissions.every(permission => ['C', 'R', 'U', 'D'].includes(permission))
            )) throw new Error();
          node.access = access as Record<string, string[]>;
        } catch {
          throw new SchemaImportError('contentTypes.schemaImportInvalidAccess', { key });
        }
      }

      if (type === 'array') {
        const itemType = itemsIndex >= 0 ? this.normaliseType(row[itemsIndex]) : 'string';
        node.items = { key: 'items', type: itemType || 'string' };
      }
      this.insertNode(properties, key.split('.').map(part => part.trim()).filter(Boolean), node);
    });

    if (!properties.length) throw new SchemaImportError('contentTypes.schemaImportNoSpreadsheetFields');
    return properties;
  }

  async readSpreadsheet(file: File): Promise<SpreadsheetRows> {
    const rows = await this.readRows(file);
    const headerIndex = rows.findIndex(row => row.filter(value => this.asText(value)).length >= 2);
    if (headerIndex < 0) throw new Error('No data header row was found.');
    const headers = rows[headerIndex].map(value => this.asText(value).trim());
    if (!headers.filter(Boolean).length) throw new Error('The data header row is empty.');

    const resultRows: SpreadsheetRows['rows'] = [];
    for (let rowIndex = headerIndex + 1; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      const hasValues = row.some(value => this.asText(value) !== '');
      // A blank row ends the table. This allows footer notes below an Excel
      // import table without treating them as invalid Tridys.
      if (!hasValues && resultRows.length) break;
      if (!hasValues) continue;
      resultRows.push({
        rowNumber: rowIndex + 1,
        values: headers.reduce((result: Record<string, unknown>, header, columnIndex) => {
          if (header) result[header] = row[columnIndex];
          return result;
        }, {})
      });
    }

    return { fileName: file.name, headers: headers.filter(Boolean), rows: resultRows };
  }

  validatePayload(values: Record<string, unknown>, schema: SchemaNode[]): { payload: Record<string, unknown>; errors: string[] } {
    return this.validateValues(values, schema, true);
  }

  /**
   * Validates only the fields supplied by an update import. Required fields are
   * deliberately not checked here: an update must be able to change one value
   * without requiring the rest of the already stored payload again.
   */
  validatePartialPayload(values: Record<string, unknown>, schema: SchemaNode[]): { payload: Record<string, unknown>; errors: string[] } {
    return this.validateValues(values, schema, false);
  }

  /**
   * Returns every payload path that a spreadsheet can supply. Object fields
   * support either one JSON value or their individual dot-notated children.
   * Object arrays intentionally remain one JSON column because spreadsheet
   * rows cannot safely express the relationship between several array items.
   */
  getImportableSchemaKeys(schema: SchemaNode[], parent = ''): string[] {
    return (schema || []).flatMap(node => {
      const key = parent ? `${parent}.${node.key}` : node.key;
      if (node.type === 'object') return [key, ...this.getImportableSchemaKeys(node.properties || [], key)];
      return [key];
    });
  }

  /** Headers used by generated Excel and CSV templates. */
  getSpreadsheetTemplateHeaders(schema: SchemaNode[], parent = ''): string[] {
    return (schema || []).flatMap(node => {
      const key = parent ? `${parent}.${node.key}` : node.key;
      return node.type === 'object' ? this.getSpreadsheetTemplateHeaders(node.properties || [], key) : [key];
    });
  }

  private validateValues(values: Record<string, unknown>, schema: SchemaNode[], validateRequired: boolean): { payload: Record<string, unknown>; errors: string[] } {
    const payload: Record<string, unknown> = {};
    const errors: string[] = [];
    const knownKeys = new Set(this.getImportableSchemaKeys(schema));

    Object.entries(values).forEach(([key, value]) => {
      if (!knownKeys.has(key)) return;
      const node = this.nodeForKey(schema, key.split('.'));
      if (!node) return;
      const converted = this.convert(value, node, key, errors, validateRequired);
      if (converted !== undefined) this.setNestedValue(payload, key.split('.'), converted);
    });

    if (validateRequired) this.validateMandatory(schema, payload, '', errors);
    return { payload, errors: [...new Set(errors)] };
  }

  private async readRows(file: File): Promise<unknown[][]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) throw new Error('The workbook has no sheets.');
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true }) as unknown[][];
  }

  private findSchemaHeader(rows: unknown[][]): number {
    return rows.findIndex(row => this.normaliseHeaders(row)
      .filter(header => this.schemaHeaderNames.has(header)).length >= 2);
  }

  private normaliseHeaders(row: unknown[]): string[] {
    return row.map(value => this.asText(value).toLowerCase().replace(/[\s_-]/g, ''));
  }

  private findHeader(headers: string[], names: string[]): number {
    return headers.findIndex(header => names.includes(header));
  }

  private insertNode(target: SchemaNode[], path: string[], leaf: SchemaNode): void {
    const [rawPart, ...rest] = path;
    const isArrayItemPath = rawPart?.endsWith('[]');
    const part = isArrayItemPath ? rawPart.slice(0, -2) : rawPart;
    if (!part) return;
    if (isArrayItemPath) {
      let arrayNode = target.find(node => node.key === part);
      if (!arrayNode) {
        arrayNode = { key: part, type: 'array', items: { key: 'items', type: 'object', properties: [] } };
        target.push(arrayNode);
      }
      arrayNode.type = 'array';
      arrayNode.items = arrayNode.items || { key: 'items', type: 'object', properties: [] };
      arrayNode.items.type = 'object';
      arrayNode.items.properties = arrayNode.items.properties || [];
      this.insertNode(arrayNode.items.properties, rest, leaf);
      return;
    }
    if (!rest.length) {
      const existing = target.find(node => node.key === part);
      if (existing) Object.assign(existing, leaf);
      else target.push(leaf);
      return;
    }
    let parent = target.find(node => node.key === part);
    if (!parent) {
      parent = { key: part, type: 'object', properties: [] };
      target.push(parent);
    }
    parent.type = 'object';
    parent.properties = parent.properties || [];
    this.insertNode(parent.properties, rest, leaf);
  }

  private normaliseType(value: unknown): SchemaNode['type'] | undefined {
    const type = this.asText(value).toLowerCase();
    const aliases: Record<string, SchemaNode['type']> = {
      string: 'string', text: 'string', number: 'number', integer: 'number', decimal: 'number',
      boolean: 'boolean', bool: 'boolean', date: 'date', array: 'array', list: 'array', object: 'object'
    };
    return aliases[type];
  }

  private convert(value: unknown, node: SchemaNode, key: string, errors: string[], validateRequired: boolean): unknown {
    if (value === null || value === undefined || this.asText(value) === '') return undefined;
    let converted: unknown;
    if (node.type === 'string') converted = this.asText(value);
    if (node.type === 'number') {
      const number = typeof value === 'number' ? value : Number(this.asText(value).replace(',', '.'));
      if (!Number.isFinite(number)) errors.push(`${key} must be a number.`);
      converted = Number.isFinite(number) ? number : undefined;
    }
    if (node.type === 'boolean') {
      const text = this.asText(value).toLowerCase();
      if (['true', 'yes', '1', 'x'].includes(text) || value === true || value === 1) converted = true;
      else if (['false', 'no', '0'].includes(text) || value === false || value === 0) converted = false;
      else errors.push(`${key} must be true or false.`);
    }
    if (node.type === 'date') {
      const date = value instanceof Date ? value : new Date(this.asText(value));
      if (Number.isNaN(date.getTime())) errors.push(`${key} must be a valid date.`);
      converted = Number.isNaN(date.getTime()) ? undefined : date;
    }
    if (node.type === 'array') {
      const items = this.arrayValues(value, node, key, errors);
      converted = items?.map((item, index) => this.convert(item, node.items || { key: 'items', type: 'string' }, `${key}[${index}]`, errors, validateRequired));
    }
    if (node.type === 'object') converted = this.convertObject(value, node, key, errors, validateRequired);
    if (converted !== undefined) this.validateValidators(converted, node, key, errors);
    return converted;
  }

  private arrayValues(value: unknown, node: SchemaNode, key: string, errors: string[]): unknown[] | undefined {
    if (Array.isArray(value)) return value;
    const text = this.asText(value);
    if (text.trimStart().startsWith('[')) {
      try {
        const parsed = JSON.parse(text);
        if (!Array.isArray(parsed)) throw new Error();
        return parsed;
      } catch {
        errors.push(`${key} must be a valid JSON array.`);
        return undefined;
      }
    }
    if (node.items?.type === 'object') {
      errors.push(`${key} must be a JSON array of objects.`);
      return undefined;
    }
    return text.split(/[;,]/).map(item => item.trim()).filter(Boolean);
  }

  private convertObject(value: unknown, node: SchemaNode, key: string, errors: string[], validateRequired: boolean): Record<string, unknown> | undefined {
    let source = value;
    if (typeof source === 'string') {
      try { source = JSON.parse(source); } catch { errors.push(`${key} must be a JSON object.`); return undefined; }
    }
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      errors.push(`${key} must be an object.`);
      return undefined;
    }
    const result: Record<string, unknown> = {};
    const properties = node.properties || [];
    Object.entries(source as Record<string, unknown>).forEach(([propertyKey, propertyValue]) => {
      const propertySchema = properties.find(property => property.key === propertyKey);
      if (!propertySchema) {
        errors.push(`${key}.${propertyKey} is not part of the schema.`);
        return;
      }
      const converted = this.convert(propertyValue, propertySchema, `${key}.${propertyKey}`, errors, validateRequired);
      if (converted !== undefined) result[propertyKey] = converted;
    });
    if (validateRequired) {
      properties.forEach(property => {
        if (this.isMandatory(property) && (result[property.key] === undefined || result[property.key] === '')) {
          errors.push(`${key}.${property.key} is required.`);
        }
      });
    }
    return result;
  }

  private validateValidators(value: unknown, node: SchemaNode, key: string, errors: string[]): void {
    (node.validators || []).forEach(validator => {
      const type = String(validator['type'] || '');
      if (!['mandatory', 'textEMail', 'email', 'textLength', 'textRegex', 'textFormat', 'textEnum', 'numericEnum', 'numericRange', 'dateRange', 'arrayLength'].includes(type)) {
        errors.push(`${key} uses unsupported validation rule "${type}"; this import cannot validate it.`);
        return;
      }
      if (type === 'mandatory') return;
      const values = Array.isArray(validator['values']) ? validator['values'] : [];
      if ((type === 'textEMail' || type === 'email') && (typeof value !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) errors.push(`${key} must be a valid email address.`);
      if (type === 'textEMail' && validator['lowercase'] === true && typeof value === 'string' && value !== value.toLowerCase()) errors.push(`${key} must use lowercase characters.`);
      if (type === 'textLength' && typeof value === 'string') {
        const min = validator['min'];
        const max = validator['max'];
        if (typeof min === 'number' && value.length < min) errors.push(`${key} is shorter than the allowed minimum.`);
        if (typeof max === 'number' && value.length > max) errors.push(`${key} is longer than the allowed maximum.`);
      }
      if (type === 'textRegex' && typeof value === 'string' && validator['regex']) {
        try { if (!new RegExp(String(validator['regex'])).test(value)) errors.push(validator['hint'] ? `${key}: ${String(validator['hint'])}` : `${key} has an invalid format.`); } catch { errors.push(`${key} has an invalid schema regex.`); }
      }
      if (type === 'textFormat' && typeof value === 'string' && !this.matchesTextFormat(value, String(validator['format'] || ''))) {
        const format = String(validator['format'] || '');
        errors.push(this.isSupportedTextFormat(format)
          ? `${key} has an invalid ${format} format.`
          : `${key} uses unsupported text format "${format}"; this import cannot validate it.`);
      }
      if (type === 'dateRange') {
        const dateValue = value instanceof Date ? value : typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
          ? new Date(`${value}T00:00:00.000Z`) : undefined;
        if (!dateValue || Number.isNaN(dateValue.getTime())) {
          errors.push(`${key} must be a date (YYYY-MM-DD) to validate its date range.`);
          return;
        }
        const minDaysAgo = validator['minDaysAgo'];
        if (minDaysAgo !== undefined && minDaysAgo !== null && minDaysAgo !== '') {
          const days = Number(minDaysAgo);
          if (!Number.isFinite(days) || days < 0) {
            errors.push(`${key} has an invalid minDaysAgo rule.`);
          } else {
            const today = new Date();
            const start = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - days);
            const end = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1);
            if (dateValue.getTime() < start || dateValue.getTime() >= end) errors.push(`${key} must be within the last ${days} days.`);
          }
        }
        for (const bound of ['min', 'max'] as const) {
          const raw = validator[bound];
          if (raw === undefined || raw === null || raw === '') continue;
          const date = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)
            ? new Date(`${raw}T00:00:00.000Z`) : undefined;
          if (!date || Number.isNaN(date.getTime())) {
            errors.push(`${key} has an unsupported ${bound} date bound; use YYYY-MM-DD.`);
          } else if (bound === 'min' && dateValue.getTime() < date.getTime()) {
            errors.push(`${key} is before the earliest allowed date.`);
          } else if (bound === 'max' && dateValue.getTime() >= date.getTime() + 24 * 60 * 60 * 1000) {
            errors.push(`${key} is after the latest allowed date.`);
          }
        }
      }
      if ((type === 'textEnum' || type === 'numericEnum') && values.length && !values.includes(value)) errors.push(`${key} must be one of the allowed values.`);
      if (type === 'numericRange' && typeof value === 'number') {
        const min = validator['min'];
        const max = validator['max'];
        const multipleOf = validator['multipleOf'];
        if (typeof min === 'number' && (validator['minExclusive'] ? value <= min : value < min)) errors.push(`${key} is below the allowed minimum.`);
        if (typeof max === 'number' && (validator['maxExclusive'] ? value >= max : value > max)) errors.push(`${key} is above the allowed maximum.`);
        if (validator['decimal'] === false && !Number.isInteger(value)) errors.push(`${key} must be a whole number.`);
        if (typeof multipleOf === 'number' && value % multipleOf !== 0) errors.push(`${key} must be a multiple of ${multipleOf}.`);
      }
      if (type === 'arrayLength' && Array.isArray(value)) {
        const min = validator['min'];
        const max = validator['max'];
        if (typeof min === 'number' && value.length < min) errors.push(`${key} must contain at least ${min} items.`);
        if (typeof max === 'number' && value.length > max) errors.push(`${key} must contain at most ${max} items.`);
      }
    });
  }

  private nodeForKey(nodes: SchemaNode[], path: string[]): SchemaNode | undefined {
    const directKey = path.join('.');
    const directNode = nodes.find(candidate => candidate.key === directKey);
    if (directNode) return directNode;

    const [part, ...rest] = path;
    const node = nodes.find(candidate => candidate.key === part);
    return rest.length ? (node?.properties ? this.nodeForKey(node.properties, rest) : undefined) : node;
  }

  private validateMandatory(nodes: SchemaNode[], payload: Record<string, unknown>, parent: string, errors: string[]): void {
    nodes.forEach(node => {
      const key = parent ? `${parent}.${node.key}` : node.key;
      const value = this.valueForKey(payload, key.split('.'));
      if (this.isMandatory(node) && (value === undefined || value === null || value === '')) errors.push(`${key} is required.`);
      // Array item fields are validated by convertObject for each parsed JSON
      // item. They are not top-level payload properties.
      if (node.type !== 'array' && node.properties && value !== undefined && value !== null) {
        this.validateMandatory(node.properties, payload, key, errors);
      }
    });
  }

  private isMandatory(node: SchemaNode): boolean {
    return node.mandatory === true || (node.validators || []).some(rule => rule['type'] === 'mandatory');
  }

  private matchesTextFormat(value: string, format: string): boolean {
    switch (format.toLowerCase()) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'uri':
      case 'url':
        try { new URL(value); return true; } catch { return false; }
      case 'uuid':
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
      case 'date':
        return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
      case 'date-time':
      case 'datetime':
        return !Number.isNaN(Date.parse(value));
      case 'duration':
        return this.isDuration(value);
      default:
        return false;
    }
  }

  private isSupportedTextFormat(format: string): boolean {
    return ['email', 'uri', 'url', 'uuid', 'date', 'date-time', 'datetime', 'duration'].includes(format.toLowerCase());
  }

  private isDuration(value: string): boolean {
    const date = '(?:\\d+Y(?:\\d+M(?:\\d+D)?)?|\\d+M(?:\\d+D)?|\\d+D)';
    const time = '(?:\\d+H(?:\\d+M(?:\\d+S)?)?|\\d+M(?:\\d+S)?|\\d+S)';
    return new RegExp(`^P(?:${date}(?:T${time})?|T${time}|\\d+W)$`).test(value);
  }

  private setNestedValue(target: Record<string, unknown>, path: string[], value: unknown): void {
    const key = path.shift();
    if (!key) return;
    if (!path.length) { target[key] = value; return; }
    const child = (target[key] as Record<string, unknown>) || {};
    target[key] = child;
    this.setNestedValue(child, path, value);
  }

  private valueForKey(source: Record<string, unknown>, path: string[]): unknown {
    return path.reduce<unknown>((value, key) => value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined, source);
  }

  private asText(value: unknown): string { return value === null || value === undefined ? '' : String(value).trim(); }
  private asBoolean(value: unknown): boolean { return ['true', 'yes', '1', 'x', 'required', 'mandatory'].includes(this.asText(value).toLowerCase()); }
}
